import "./Checkpoints.scss";
import classNames from "classnames";
import {Fragment, useEffect, useRef, useState} from "react";
import CreateCheckpointModal from "./CreateCheckpointModal";
import {useParams} from "react-router-dom";
import {isEqual} from "lodash";
import {diffArrays} from "diff";
import useVariables from "../../../contexts/variablesContext/useVariables";
import useCheckpointsList from "../../../contexts/checkpointsListContext/useCheckpointsList";
import {getNestedValue, parseIfString} from "../../../utils/Utils";
import Button from "../../Button/Button";
import Icon, {arrowIcon, plusIcon} from "../../Icon/Icon";
import DocumentActions from "../../../api/DocumentActions";
import CheckpointActions from "../../../api/CheckpointActions";

const VIEW = {
    CHECKPOINT: "CHECKPOINT",
    DIFFERENCE: "DIFFERENCE",
}

export const Checkpoints = ({onShowEditor, flushEditorChanges}) => {
    const {checkpointsList, setCheckpointsList} = useCheckpointsList();
    const {setDiffVariables} = useVariables();
    const [showModal, setShowModal] = useState(false);
    const [active, setActive] = useState({id: null, type: null});
    const [checkpointEditMode, setCheckpointEditMode] = useState(false);
    const [checkpointName, setCheckpointName] = useState("");
    const checkpointInputRef = useRef(null);
    const {id} = useParams();

    const mapById = (doc) => {
        const map = new Map()
        if (!doc?.content) return map

        doc.content.forEach(node => {
            if (["paragraph", "heading", "orderedList", "bulletList", "table", "variableTable", "variable"].includes(node.type) && node.attrs?.id) {
                map.set(node.attrs.id, node)
            }
        })

        return map
    }

    const applyToplessClass = (nodes) => {
        return nodes.map((node, index) => {
            if (!isNodeEmpty(node) || index === 0) return node;

            const previousNode = nodes[index - 1];
            const isPreviousAlsoEmpty = previousNode.attrs?.class?.includes('diff-empty-removed') || previousNode.attrs?.class?.includes('diff-empty-added');

            if (!isPreviousAlsoEmpty) return node;

            return {...node, attrs: {...node.attrs, class: `${node.attrs.class} topless`}};
        })
    }

    const isNodeEmpty = (node) => {
        if (!node.content) return true;
        if (node.type === "table") return false;
        return !node.content?.some(c => c.text?.length || c.type === "variable" || c.type === "variableList")
    }

    const getDiffClass = (node, type) => {
        return isNodeEmpty(node) ? `diff-empty-${type}` : `diff-${type}`;
    }

    const normalizeTextNodes = (content) => {
        if (!content) return content;

        const result = [];

        for (const node of content) {
            if (node.type !== "text") {
                result.push(node);
                continue;
            }

            const marks = node.marks?.filter(mark => mark.type !== "comment") ?? undefined;

            const normalizedNode = {
                ...node,
                marks: marks?.length ? marks : undefined
            };

            const prev = result[result.length - 1];

            if (prev?.type === "text" && isEqual(prev.marks, normalizedNode.marks)) {
                result[result.length - 1] = {...prev, text: prev.text + normalizedNode.text};
            } else {
                result.push(normalizedNode);
            }
        }

        return result;
    }

    const flattenToWords = (inlineNodes) => {
        const words = [];
        if (!inlineNodes) return words;

        inlineNodes.forEach(node => {
            if (node.type === "hardBreak") {
                words.push({type: "hardBreak", marks: []});
                return;
            }

            if (node.type === "variable" || node.type === "variableList") {
                words.push({type: node.type, attrs: node.attrs, marks: node.marks || []})
                return;
            }

            const parts = node.text ? node.text.split(/(\s+)/) : [];
            parts.forEach(part => {
                if (part) words.push({type: "text", text: part, marks: node.marks || []});
            })
        })

        return words;
    }

    const marksEqual = (oldMarks, newMarks) => {
        const filteredOld = oldMarks.filter(m => m.type !== "comment");
        const filteredNew = newMarks.filter(m => m.type !== "comment");

        if (filteredOld.length !== filteredNew.length) return false;
        return JSON.stringify(filteredOld) === JSON.stringify(filteredNew);
    }

    const diffNodesToInlineContent = (oldNode, newNode, oldVariables, newVariables) => {
        const oldWords = flattenToWords(oldNode?.content || []);
        const newWords = flattenToWords(newNode?.content || []);

        const changes = diffArrays(oldWords, newWords, {
            comparator: (a, b) => {
                if (a.type !== b.type) return false;
                if (a.type === "hardBreak") return true;
                if (a.type === "variable" || a.type === "variableList") {
                    if (JSON.stringify(a.attrs) !== JSON.stringify(b.attrs)) return false;
                    if (!marksEqual(a.marks, b.marks)) return false;

                    const oldValue = getNestedValue(oldVariables, a.attrs.path);
                    const newValue = getNestedValue(newVariables, b.attrs.path);
                    return JSON.stringify(oldValue) === JSON.stringify(newValue);
                }

                return a.text === b.text && marksEqual(a.marks, b.marks);
            }
        })

        const mergedChanges = []
        let i = 0

        while (i < changes.length) {
            const current = changes[i];
            const next = changes[i + 1];

            if (
                current.removed &&
                next?.added &&
                current.value.length === next.value.length &&
                current.value.every((w, idx) => {
                    const b = next.value[idx];

                    if (w.type !== b.type) return false;
                    if (w.type === "variable" || w.type === "variableList") return w.attrs?.path === b.attrs?.path;

                    return w.text === b.text;
                })
            ) {
                mergedChanges.push({modified: true, value: next.value, oldValue: current.value});
                i += 2;
            } else {
                mergedChanges.push(current);
                i++;
            }
        }

        const result = [];

        const pushWord = (w, markType = null) => {
            if (w.type === "hardBreak") {
                result.push({type: "hardBreak"});
                return;
            }

            const marks = markType ? [...w.marks, {type: markType}] : w.marks;
            result.push({...w, marks});
        }

        mergedChanges.forEach(part => {
            if (part.modified) {
                part.oldValue.forEach((w) => {
                    if (w.type === "variableList") return;
                    if (w.type === "variable") {
                        const val = getNestedValue(oldVariables, w.attrs.path);
                        const text = val !== undefined && val !== null ? String(val) : w.attrs.path;
                        result.push({type: "text", text, marks: [...w.marks, {type: "diffRemoved"}]});
                        return;
                    }
                    pushWord(w, "diffRemoved");
                })

                part.value.forEach((w) => {
                    if (w.type === "variableList") {
                        result.push(w);
                        return;
                    }
                    if (w.type === "variable") {
                        const val = getNestedValue(newVariables, w.attrs.path);
                        const text = val !== undefined && val !== null ? String(val) : w.attrs.path;
                        result.push({type: "text", text, marks: [...w.marks, {type: "diffAdded"}]});
                        return;
                    }
                    pushWord(w, "diffAdded");
                })
                return;
            }

            if (part.removed) {
                part.value.filter(w => w.type !== "hardBreak").forEach(w => pushWord(w, "diffRemoved"));
                return;
            }

            if (part.added) {
                part.value.forEach(w => pushWord(w, "diffAdded"));
                return;
            }

            part.value.forEach(w => pushWord(w));
        })

        return result;
    }

    const diffListNodes = (oldNode, newNode) => {
        const oldItems = oldNode.content || [];
        const newItems = newNode.content || [];
        const result = [];

        const minLen = Math.min(oldItems.length, newItems.length);

        for (let i = 0; i < minLen; i++) {
            const oldItem = oldItems[i];
            const newItem = newItems[i];

            if (!isEqual(oldItem.content, newItem.content)) {
                result.push({
                    ...newItem,
                    content: newItem.content.map((node, j) => {
                        const oldNode = oldItem.content?.[j];
                        if (!oldNode || isEqual(oldNode.content, node.content)) return node;

                        return {
                            ...node,
                            content: diffNodesToInlineContent(oldNode, node)
                        };
                    })
                });
            } else {
                result.push(newItem);
            }
        }

        if (newItems.length > oldItems.length) {
            newItems.slice(minLen).forEach(newItem => {
                result.push({
                    ...newItem,
                    content: newItem.content.map(node => ({
                        ...node,
                        content: node.content?.map(textNode => ({
                            ...textNode,
                            marks: [...(textNode.marks || []), {type: "diffAdded"}]
                        }))
                    }))
                });
            });
        }

        if (oldItems.length > newItems.length) {
            oldItems.slice(minLen).forEach(oldItem => {
                result.push({
                    ...oldItem,
                    content: oldItem.content.map(node => ({
                        ...node,
                        content: node.content?.map(textNode => ({
                            ...textNode,
                            marks: [...(textNode.marks || []), {type: "diffRemoved"}]
                        }))
                    }))
                });
            });
        }

        return {...newNode, content: result};
    };

    const diffTable = (oldTable, newTable, oldVariables, newVariables) => {
        const oldRows = oldTable.content ?? [];
        const newRows = newTable.content ?? [];
        const maxRows = Math.max(oldRows.length, newRows.length);
        const resultRows = [];

        for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            const oldRow = oldRows[rowIndex];
            const newRow = newRows[rowIndex];

            if (!oldRow && newRow) {
                resultRows.push({
                    ...newRow,
                    content: newRow.content.map(cell => ({
                        ...cell,
                        content: cell.content.map(node => ({
                            ...node,
                            attrs: {...node.attrs, class: getDiffClass(node, "added")}
                        }))
                    }))
                })
                continue;
            }

            if (oldRow && !newRow) {
                resultRows.push({
                    ...oldRow,
                    content: oldRow.content.map(cell => ({
                        ...cell,
                        content: cell.content.map(node => ({
                            ...node,
                            attrs: {...node.attrs, class: getDiffClass(node, "removed")}
                        }))
                    }))
                })
                continue;
            }

            const oldCells = oldRow.content ?? [];
            const newCells = newRow.content ?? [];
            const maxCells = Math.max(oldCells.length, newCells.length);
            const resultCells = [];

            for (let cellIndex = 0; cellIndex < maxCells; cellIndex++) {
                const oldCell = oldCells[cellIndex];
                const newCell = newCells[cellIndex];

                if (!oldCell && newCell) {
                    resultCells.push({
                        ...newCell,
                        content: newCell.content.map(node => ({
                            ...node,
                            attrs: {...node.attrs, class: getDiffClass(node, "added")}
                        }))
                    })
                    continue;
                }

                if (oldCell && !newCell) {
                    resultCells.push({
                        ...oldCell,
                        content: oldCell.content.map(node => ({
                            ...node,
                            attrs: {...node.attrs, class: getDiffClass(node, "removed")}
                        }))
                    })
                    continue;
                }

                const oldCellContent = oldCell.content ?? [];
                const newCellContent = newCell.content ?? [];

                const oldById = new Map(oldCellContent.map(n => [n.attrs?.id, n]));
                const newById = new Map(newCellContent.map(n => [n.attrs?.id, n]));

                const resultContent = newCellContent.map(node => {
                    const id = node.attrs?.id;

                    if (id && oldById.has(id)) {
                        const oldNode = oldById.get(id)
                        if (!isEqual(oldNode.content, node.content)) {
                            return {
                                ...node,
                                content: diffNodesToInlineContent(oldNode, node, oldVariables, newVariables)
                            }
                        }

                        return node;
                    }

                    return {
                        ...node,
                        attrs: {...node.attrs, class: getDiffClass(node, "added")}
                    };
                })

                oldCellContent.forEach(node => {
                    const id = node.attrs?.id
                    if (id && !newById.has(id)) {
                        resultContent.push({
                            ...node,
                            attrs: {...node.attrs, class: getDiffClass(node, "removed")}
                        })
                    }
                })

                resultCells.push({...newCell, content: resultContent});
            }

            resultRows.push({...newRow, content: resultCells});
        }

        return {...newTable, content: resultRows};
    }

    const diffVariableTable = (oldTable, newTable, oldVariables, newVariables) => {
        const oldSourcePath = oldTable.attrs?.sourcePath;
        const newSourcePath = newTable.attrs?.sourcePath;
        const oldColumns = oldTable.attrs?.columns ?? [];
        const newColumns = newTable.attrs?.columns ?? [];

        const oldList = getNestedValue(oldVariables, oldSourcePath) ?? [];
        const newList = getNestedValue(newVariables, newSourcePath) ?? [];

        const hasChanges  =
            oldSourcePath !== newSourcePath ||
            JSON.stringify(oldColumns) !== JSON.stringify(newColumns) ||
            JSON.stringify(oldList) !== JSON.stringify(newList)

        if (hasChanges) {
            return {...newTable, attrs: {...newTable.attrs, class: "diff-modified", oldColumns: oldColumns}}
        }

        return newTable;
    }

    const getDiffDocument = (oldDoc, newDoc, oldVariables, newVariables) => {
        const isList = (node) => ["orderedList", "bulletList"].includes(node.type)

        if (!oldDoc?.content?.length && !newDoc?.content?.length) {
            return {type: "doc", content: []}
        }

        if (!oldDoc?.content?.length) {
            return {
                type: "doc",
                content: applyToplessClass(newDoc.content.map(node => {
                    if (node.type === "paragraph" || node.type === "heading") return {
                        ...node,
                        attrs: {...node.attrs, class: getDiffClass(node, "added")}
                    }
                    if (isList(node)) return {
                        ...node,
                        attrs: {...node.attrs, class: "diff-added"}
                    }
                    if (node.type === "table" || node.type === "variableTable") return {
                        ...node,
                        attrs: {...node.attrs, class: "diff-added"}
                    }
                    return node
                }))
            }
        }

        if (!newDoc?.content?.length) {
            return {
                type: "doc",
                content: applyToplessClass(oldDoc.content.map(node => {
                    if (node.type === "paragraph" || node.type === "heading") return {
                        ...node,
                        attrs: {...node.attrs, class: getDiffClass(node, "removed")}
                    }
                    if (isList(node)) return {
                        ...node,
                        attrs: {...node.attrs, class: "diff-removed"}
                    }
                    if (node.type === "table" || node.type === "variableTable") return {
                        ...node,
                        attrs: {...node.attrs, class: "diff-removed"}
                    }
                    return node
                }))
            }
        }

        const oldDocNodesById = mapById(oldDoc)
        const newDocNodesById = mapById(newDoc)

        const result = []

        newDoc.content.forEach(node => {
            const id = node.attrs?.id

            if (node.type === "table") {
                if (id && oldDocNodesById.has(id)) {
                    const oldTable = oldDocNodesById.get(id);
                    result.push(diffTable(oldTable, node, oldVariables, newVariables));
                } else {
                    result.push({
                        ...node,
                        attrs: {...node.attrs, class: "diff-added"}
                    });
                }
                return;
            }

            if (node.type === "variableTable") {
                if (id && oldDocNodesById.has(id)) {
                    result.push(diffVariableTable(
                        oldDocNodesById.get(id),
                        node,
                        oldVariables,
                        newVariables
                    ));
                } else {
                    result.push({...node, attrs: {...node.attrs, class: "diff-added"}});
                }

                return;
            }

            if (node.type !== "paragraph" && node.type !== "heading" && !isList(node)) {
                result.push(node)
                return
            }

            if (id && oldDocNodesById.has(id)) {
                const oldDocNode = oldDocNodesById.get(id)
                const oldDocIndex = oldDoc.content.findIndex(n => n.attrs?.id === id)

                oldDoc.content.slice(0, oldDocIndex).forEach(removedNode => {
                    if (node.type !== removedNode.type && !(isList(node) && isList(removedNode)) && !(node.type === "heading" && removedNode.type === "heading")) return
                    const removedId = removedNode.attrs?.id
                    if (removedId && !newDocNodesById.has(removedId) && !result.some(r => r.attrs?.id === removedId)) {
                        result.push({
                            ...removedNode,
                            attrs: {
                                ...removedNode.attrs,
                                class: isList(removedNode) ? "diff-removed" : getDiffClass(removedNode, "removed")
                            }
                        })
                    }
                })

                const contentChanged = !isEqual(normalizeTextNodes(oldDocNode.content), normalizeTextNodes(node.content));

                const variablesChanged = (node.content || []).some(inline => {
                    if (inline.type !== "variable" && inline.type !== "variableList") return false;
                    const oldVal = getNestedValue(oldVariables, inline.attrs.path);
                    const newVal = getNestedValue(newVariables, inline.attrs.path);
                    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                })

                if (contentChanged || variablesChanged) {
                    if (isList(node)) {
                        result.push(diffListNodes(oldDocNode, node));
                    } else {
                        result.push({
                            ...node,
                            content: diffNodesToInlineContent(oldDocNode, node, oldVariables, newVariables)
                        })
                    }
                } else {
                    result.push(node)
                }

                return
            }

            result.push({
                ...node,
                attrs: {
                    ...node.attrs,
                    class: isList(node) ? "diff-added" : getDiffClass(node, "added")
                }
            })
        })

        oldDoc.content.forEach(node => {
            if (node.type !== "paragraph" && node.type !== "heading"  && node.type !== "table" && node.type !== "variableTable" && !isList(node)) return;

            const id = node.attrs?.id;
            if (!id || newDocNodesById.has(id) || result.some(r => r.attrs?.id === id)) return;

            result.push({
                ...node,
                attrs: {
                    ...node.attrs,
                    class: isList(node) || node.type === "table" || node.type === "variableTable"
                        ? "diff-removed"
                        : getDiffClass(node, "removed")
                }
            })
        })

        return {type: "doc", content: applyToplessClass(result)}
    }

    const handleCreateCheckpoint = (data) => {
        CheckpointActions.save(data, (response) => {
            setCheckpointsList((prev) => [...prev, response]);
            setActive({id: response.id, type: VIEW.CHECKPOINT});
            setDiffVariables(null);
            onShowEditor({
                content: response.content,
                editable: true,
                diffMode: false,
                checkpointContent: response.content,
                checkpointId: response.id,
                nextCheckpointId: response.id,
            })
        })
    }

    const handleShow = (checkpoint, index, mode) => {
        if (active.type === VIEW.CHECKPOINT && mode === VIEW.CHECKPOINT && active.id === checkpoint.id) {
            setCheckpointName(checkpoint.name);
            setCheckpointEditMode(true);
            return;
        }

        const latestContent = flushEditorChanges();

        setActive({id: checkpoint.id, type: mode});
        const lastCheckpoint = index === checkpointsList.length - 1;

        if (mode === VIEW.DIFFERENCE) {
            const oldCheckpoint = checkpointsList[index - 1] ?? {content: null, variables: {}};
            const newCheckpoint = lastCheckpoint && latestContent
                ? {...checkpoint, content: latestContent}
                : checkpoint;
            setDiffVariables(oldCheckpoint.variables);
            const diffContent = getDiffDocument(
                oldCheckpoint.content,
                newCheckpoint.content,
                parseIfString(oldCheckpoint.variables),
                parseIfString(newCheckpoint.variables)
            );

            onShowEditor({
                content: diffContent,
                editable: lastCheckpoint,
                diffMode: true,
                checkpointContent: newCheckpoint.content,
                checkpointId: checkpointsList[checkpointsList.length - 1].id,
                nextCheckpointId: checkpoint.id
            })
        }

        if (mode === VIEW.CHECKPOINT) {
            setDiffVariables(null);
            if (lastCheckpoint) {
                DocumentActions.get(id, (response) => {
                    onShowEditor({
                        content: response.content,
                        editable: true,
                        diffMode: false,
                        checkpointContent: checkpoint.content,
                        checkpointId: checkpoint.id,
                        nextCheckpointId: checkpoint.id
                    })

                });
            } else {
                onShowEditor({
                    content: checkpoint.content,
                    editable: false,
                    diffMode: false,
                    checkpointContent: checkpoint.content,
                    checkpointId: checkpoint.id,
                    nextCheckpointId: checkpoint.id
                })

            }
        }
    };

    const handleUpdateCheckpointName = () => {
        CheckpointActions.save({id: active.id, name: checkpointName}, () => {
            setCheckpointsList(prev =>
                prev.map(item =>
                    item.id === active.id
                        ? {...item, name: checkpointName}
                        : item
                )
            );
        });
    };

    useEffect(() => {
        CheckpointActions.getAll(id, (response) => {
            setCheckpointsList(response);
            const lastCheckpoint = response.at(-1);
            setActive({id: lastCheckpoint.id, type: VIEW.CHECKPOINT})
        })
    }, []);

    useEffect(() => {
        if (!checkpointEditMode) return;

        const handleClickOutside = (e) => {
            if (checkpointInputRef.current && !checkpointInputRef.current.contains(e.target)) {
                if (checkpointName.trim() !== "") {
                    handleUpdateCheckpointName();
                }
                setCheckpointEditMode(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === "Enter") {
                if (checkpointName.trim() !== "") {
                    handleUpdateCheckpointName();
                }
                setCheckpointEditMode(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [checkpointName, checkpointEditMode]);

    return <div className="checkpoints">
        {showModal && <CreateCheckpointModal onSave={handleCreateCheckpoint} onClose={() => setShowModal(false)}/>}

        {checkpointsList?.length > 0 && checkpointsList.map((checkpoint, index) =>
            <Fragment key={index}>
                <Button
                    className={classNames("checkpoint", {active: active.id === checkpoint.id && active.type === VIEW.CHECKPOINT})}
                    key={index}
                    onClick={() => handleShow(checkpoint, index, VIEW.CHECKPOINT)}
                    disabled={checkpointEditMode && active.id === checkpoint.id}
                >
                    {
                        active.id === checkpoint.id && checkpointEditMode
                        ? <input
                            ref={checkpointInputRef}
                            className="checkpoint-input"
                            name="checkpointName"
                            value={checkpointName}
                            onChange={(e) => setCheckpointName(e.target.value)}
                        />
                        :
                        <span>{checkpoint.name}</span>
                    }
                </Button>
                <Button
                    className={classNames("difference", {
                        active: active.id === checkpoint.id && active.type === VIEW.DIFFERENCE
                    })}
                    onClick={() => handleShow(checkpoint, index, VIEW.DIFFERENCE)}
                >
                    <Icon svg={arrowIcon}/>
                </Button>
            </Fragment>
        )}
        <Button
            className="new-checkpoint"
            onClick={() => setShowModal(true)}
        >
            <Icon svg={plusIcon}/>
            CheckPoint
        </Button>
    </div>
}

export default Checkpoints